import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useShop } from "@/store";
import { M, P, J, byId } from "@/data/catalogue";
import { EASE } from "@/components/motion";

const overlayAnim = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.25 },
};
const drawerAnim = {
  initial: { x: "100%" },
  animate: { x: 0 },
  exit: { x: "100%" },
  transition: { duration: 0.45, ease: EASE },
};

function DrawerShell({ title, children, footer, testid }) {
  const { setDrawer } = useShop();
  return (
    <motion.aside className="drawer" role="dialog" aria-label={title} data-testid={testid} {...drawerAnim}>
      <div className="hd">
        <span>{title}</span>
        <button className="icon" data-testid={`${testid}-close`} aria-label="Close" onClick={() => setDrawer(null)}><X /></button>
      </div>
      <div className="bd">{children}</div>
      {footer && <div className="ft">{footer}</div>}
    </motion.aside>
  );
}

function CartDrawer() {
  const { t, cart, subtotal, money, setQty, removeCart, setDrawer } = useShop();
  const left = Math.max(0, 250 - subtotal);
  const ship = subtotal >= 250 || !subtotal ? 0 : 12;
  return (
    <DrawerShell
      testid="cart-drawer"
      title={`${t("cart.h")} (${cart.reduce((a, b) => a + b.q, 0)})`}
      footer={cart.length > 0 && (
        <>
          <div style={{ fontSize: 12.5, color: "var(--fg-3)" }} data-testid="cart-shipping-note">
            {left ? t("cart.toFree").replace("{x}", money(left)) : t("cart.freeOk")}
          </div>
          <div className="progress"><i style={{ width: `${Math.min(100, (subtotal / 250) * 100)}%` }} /></div>
          <div className="tot"><span>{t("cart.sub")}</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
          <div className="tot"><span>{t("cart.ship")}</span><span>{ship ? money(ship) : t("cart.free")}</span></div>
          <div className="tot big"><span>Total</span><span data-testid="cart-total">{money(subtotal + ship)}</span></div>
          <Link className="btn primary" to="/checkout" data-testid="cart-checkout-button" onClick={() => setDrawer(null)} style={{ width: "100%", justifyContent: "center" }}>
            {t("cart.checkout")}
          </Link>
        </>
      )}
    >
      {cart.length === 0 ? (
        <div className="empty">
          <div className="big">{t("cart.empty")}</div>
          {t("cart.emptyp")}
          <div style={{ marginTop: 20 }}>
            <button className="btn" data-testid="cart-continue" onClick={() => setDrawer(null)}>{t("cart.cont")}</button>
          </div>
        </div>
      ) : (
        cart.map((l) => {
          const p = byId(l.id);
          if (!p) return null;
          return (
            <div className="line" key={l.id} data-testid={`cart-line-${p.id}`}>
              <img src={M[p.img]} alt={p.n} />
              <div>
                <Link className="n" to={`/product/${p.id}`} onClick={() => setDrawer(null)}>{p.n}</Link>
                <small>{p.one ? t("pdp.one") : t("pdp.lim")}</small>
                <div className="qty" aria-label={t("cart.qty")}>
                  <button data-testid={`cart-minus-${p.id}`} disabled={p.one} onClick={() => setQty(p.id, l.q - 1)}>−</button>
                  <span>{l.q}</span>
                  <button data-testid={`cart-plus-${p.id}`} disabled={p.one} onClick={() => setQty(p.id, l.q + 1)}>+</button>
                </div>
                <button className="rm" data-testid={`cart-remove-${p.id}`} onClick={() => removeCart(p.id)}>{t("cart.rm")}</button>
              </div>
              <div className="p">{money(p.price * l.q)}</div>
            </div>
          );
        })
      )}
    </DrawerShell>
  );
}

function WishDrawer() {
  const { t, wish, money, toggleWish, addCart, setDrawer } = useShop();
  return (
    <DrawerShell testid="wishlist-drawer" title={`${t("wish.h")} (${wish.length})`}>
      {wish.length === 0 ? (
        <div className="empty">
          <div className="big">{t("wish.empty")}</div>
          {t("wish.emptyp")}
        </div>
      ) : (
        wish.map((id) => {
          const p = byId(id);
          if (!p) return null;
          return (
            <div className="line" key={id} data-testid={`wishlist-line-${id}`}>
              <img src={M[p.img]} alt={p.n} />
              <div>
                <Link className="n" to={`/product/${id}`} onClick={() => setDrawer(null)}>{p.n}</Link>
                <small>{money(p.price)}</small>
                <button className="rm" data-testid={`wishlist-add-${id}`} onClick={() => { addCart(id); setDrawer({ type: "cart" }); }}>{t("wish.add")}</button>{" "}
                <button className="rm" data-testid={`wishlist-remove-${id}`} onClick={() => toggleWish(id)}>{t("cart.rm")}</button>
              </div>
              <div className="p">{money(p.price)}</div>
            </div>
          );
        })
      )}
    </DrawerShell>
  );
}

function SearchDrawer() {
  const { t, L, money, setDrawer } = useShop();
  const [q, setQ] = useState("");
  const v = q.toLowerCase().trim();
  const ps = v ? P.filter((p) => (p.n + " " + p.pattern + " " + L(p).d + " " + p.tags.join(" ")).toLowerCase().includes(v)) : [];
  const js = v ? J.filter((j) => (L(j).t + " " + L(j).s).toLowerCase().includes(v)) : [];
  return (
    <DrawerShell testid="search-drawer" title={t("search.label")}>
      <div className="search" style={{ padding: "0 0 14px" }}>
        <input
          autoFocus
          data-testid="search-input"
          placeholder={t("search.ph")}
          aria-label="Search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="sres" data-testid="search-results">
        {v && ps.length + js.length === 0 && <p className="empty">{t("search.none")}</p>}
        {ps.map((p) => (
          <Link key={p.id} to={`/product/${p.id}`} data-testid={`search-result-${p.id}`} onClick={() => setDrawer(null)}>
            <img src={M[p.img]} alt="" />
            <span>{p.n}<br /><small style={{ color: "var(--fg-3)" }}>{p.pattern}</small></span>
            <span>{money(p.price)}</span>
          </Link>
        ))}
        {js.map((j) => (
          <Link key={j.id} to={`/journal/${j.id}`} data-testid={`search-result-${j.id}`} onClick={() => setDrawer(null)}>
            <img src={M[j.img]} alt="" />
            <span>{L(j).t}</span>
            <span className="eyebrow mute">{L(j).c}</span>
          </Link>
        ))}
      </div>
    </DrawerShell>
  );
}

function QuickView({ id }) {
  const { t, L, money, addCart, setDrawer } = useShop();
  const p = byId(id);
  if (!p) return null;
  return (
    <motion.div className="modalwrap" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }} transition={{ duration: 0.35, ease: EASE }}>
      <div className="modal">
        <div className="box" role="dialog" aria-label={p.n} data-testid="quickview-modal">
          <div><img src={M[p.img]} alt={p.n} /></div>
          <div className="in">
            <div className="eyebrow">{p.one ? t("pdp.one") : t("pdp.lim")}</div>
            <h2 style={{ fontSize: 40, margin: "8px 0 6px" }}>{p.n}</h2>
            <div className="price" style={{ fontSize: 20, fontVariantNumeric: "tabular-nums" }}>{money(p.price)}</div>
            <p className="lede" style={{ margin: "16px 0 22px", fontSize: 15 }}>{L(p).d}</p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn primary" data-testid="quickview-add-button" onClick={() => { addCart(id); setDrawer({ type: "cart" }); }}>
                {t("pdp.add")}
              </button>
              <Link className="btn" to={`/product/${p.id}`} data-testid="quickview-details-link" onClick={() => setDrawer(null)}>{t("pdp.details")}</Link>
            </div>
            <div className="trust">
              <div><svg viewBox="0 0 24 24"><path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.5A4 4 0 0 1 19 11c0 5.5-7 10-7 10z" /><path d="M9 11h6" /></svg> {t("pdp.hand")}</div>
              <div><svg viewBox="0 0 24 24"><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="m9 12 2 2 4-4" /></svg> {t("pdp.ret")}</div>
            </div>
          </div>
          <button className="icon x" data-testid="quickview-close" aria-label="Close" onClick={() => setDrawer(null)}><X /></button>
        </div>
      </div>
    </motion.div>
  );
}

function MobileNav() {
  const { t, lang, setLang, setDrawer } = useShop();
  const links = ["shop", "story", "artisans", "impact", "journal", "wholesale"];
  return (
    <motion.div className="mobilenav" role="dialog" data-testid="mobile-nav" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      <button className="icon x" data-testid="mobile-nav-close" aria-label="Close" onClick={() => setDrawer(null)}><X /></button>
      {links.map((k, i) => (
        <motion.div key={k} initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 + i * 0.05, duration: 0.4, ease: EASE }}>
          <Link to={`/${k}`} data-testid={`mobile-nav-${k}`} onClick={() => setDrawer(null)}>{t(`nav.${k}`)}</Link>
        </motion.div>
      ))}
      <motion.div initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.42, duration: 0.4, ease: EASE }}>
        <Link to="/help" data-testid="mobile-nav-help" onClick={() => setDrawer(null)}>{t("foot.help")}</Link>
      </motion.div>
      <div className="lang">
        <button data-testid="mobile-lang-en" className={lang === "en" ? "on" : ""} onClick={() => setLang("en")}>EN</button>
        <button data-testid="mobile-lang-es" className={lang === "es" ? "on" : ""} onClick={() => setLang("es")}>ES</button>
      </div>
    </motion.div>
  );
}

export default function DrawerHost() {
  const { drawer, setDrawer } = useShop();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawer(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setDrawer]);
  return (
    <AnimatePresence>
      {drawer && (
        <motion.div key="scrim" style={{ position: "fixed", inset: 0, zIndex: 180 }} {...(drawer.type === "mobile" ? {} : overlayAnim)}>
          {drawer.type !== "mobile" && <div className="overlay" data-testid="drawer-overlay" onClick={() => setDrawer(null)} />}
          {drawer.type === "cart" && <CartDrawer />}
          {drawer.type === "wish" && <WishDrawer />}
          {drawer.type === "search" && <SearchDrawer />}
          {drawer.type === "quick" && <QuickView id={drawer.id} />}
          {drawer.type === "mobile" && <MobileNav />}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
